<script setup lang="ts">
import { computed } from 'vue'
import { useExtendedI18n } from '@/i18n'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useAddressStore } from '@/modules/shared/stores/address-store'
import { validMoney } from '@/modules/shared/utils/money'
import { VAlert, VButton, VIcon, VSection } from '@/modules/shared/components'
import type { AddOn, Subscription } from '../../../types'
import type { OnboardingStep } from '@/modules/shared/composables/use-onboarding'
import OnboardingReviewItem from '@/modules/shared/components/onboarding/OnboardingReviewItem.vue'
import OnboardingReviewSection from '@/modules/shared/components/onboarding/OnboardingReviewSection.vue'
import OnboardingPlanSummary from '@/modules/shared/components/onboarding/OnboardingPlanSummary.vue'

const props = withDefaults(
  defineProps<{
    form: any
    steps: OnboardingStep[]
    subscriptionOptions: Subscription[]
    addOnOptions: AddOn[]
    selectedPlan?: Subscription | null
    capitalCallFrequencyOptions: { label: string; value: string }[]
    ownershipTypeOptions: { label: string; value: string }[]
    adminOptions: { label: string; value: string }[]
    totalDue: string
    isNewRecord: boolean
    raisingStatus: string
    files: File[]
    existingFiles?: string[]
    isEditMode?: boolean
  }>(),
  {
    isEditMode: false,
  },
)

const emit = defineEmits<{
  (e: 'navigate', stepIndex: number): void
  (e: 'delete'): void
}>()

const { n } = useExtendedI18n()
const authStore = useAuthStore()
const addressStore = useAddressStore()

const stepIndex = (key: string) => props.steps.findIndex((s) => s.key === key)

// ── Subscription summary ────────────────────────────────────────

const selectedSubscription = computed(
  () =>
    props.selectedPlan || props.subscriptionOptions.find((s) => props.form.selected_services.includes(s.value)) || null,
)

const selectedAddOns = computed(() => props.addOnOptions.filter((a) => props.form.selected_services.includes(a.value)))

const hasSubscription = computed(
  () => props.form.is_venture360_the_administrator && props.form.selected_services.length > 0,
)

// ── Helpers ─────────────────────────────────────────────────────

const val = (v: any) => (v === null || v === undefined || v === '' ? '—' : String(v))

const formatCurrency = (money: any) => {
  if (!validMoney(money)) return '—'
  return n(money, 'currency')
}

const formatPercent = (v: any) => {
  if (v === null || v === undefined || v === '') return '—'
  return `${v}%`
}

const lookupLabel = (options: { label: string; value: string }[], value: string) => {
  if (!value) return '—'
  return options.find((o) => o.value === value)?.label ?? val(value)
}

const adminNames = computed(() => {
  if (!props.form.admin_ids?.length) return '—'
  return props.form.admin_ids
    .map((id: string) => props.adminOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ')
})

const stateName = (code: string) => {
  if (!code) return ''
  return addressStore.stateOptions.find((o) => o.value === code)?.label ?? code
}

const countryName = (code: string) => {
  if (!code) return ''
  return addressStore.countryOptions.find((o) => o.value === code)?.label ?? code
}

const addressLine = computed(() => {
  const parts = [
    props.form.street1,
    props.form.street2,
    props.form.city,
    stateName(props.form.state),
    props.form.zip,
    countryName(props.form.country),
  ].filter(Boolean)
  return parts.length ? parts.join(', ') : '—'
})

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ── Review sections ─────────────────────────────────────────────

interface ReviewRow {
  label: string
  value: string
  stepIndex: number
}

interface ReviewSection {
  title: string
  rows: ReviewRow[]
}

const sections = computed<ReviewSection[]>(() => {
  const result: ReviewSection[] = []
  const si = stepIndex

  // Fund configuration
  const configRows: ReviewRow[] = [
    {
      label: 'V360 administrator',
      value: props.form.is_venture360_the_administrator ? 'Yes' : 'No',
      stepIndex: si('config'),
    },
  ]
  if (props.form.is_venture360_the_administrator) {
    configRows.push({
      label: 'Forming fund',
      value: props.form.is_venture360_forming_this_fund ? 'Venture360' : 'Self-formed',
      stepIndex: si('config'),
    })
    configRows.push({
      label: 'Billing frequency',
      value: val(props.form.selected_billing_frequency),
      stepIndex: si('config'),
    })
    if (selectedSubscription.value) {
      configRows.push({
        label: 'Plan',
        value: selectedSubscription.value.title,
        stepIndex: si('config'),
      })
    }
    if (selectedAddOns.value.length) {
      configRows.push({
        label: 'Add-ons',
        value: selectedAddOns.value.map((a) => a.title).join(', '),
        stepIndex: si('config'),
      })
    }
  }
  result.push({ title: 'Fund configuration', rows: configRows })

  // General structure
  result.push({
    title: 'General structure',
    rows: [
      { label: 'Legal name', value: val(props.form.legal_name), stepIndex: si('structure') },
      { label: 'Fund name', value: val(props.form.name), stepIndex: si('structure') },
      { label: 'Legal structure', value: val(props.form.type), stepIndex: si('structure') },
      {
        label: 'State of incorporation',
        value: props.form.state_of_incorporation ? stateName(props.form.state_of_incorporation) : '—',
        stepIndex: si('structure'),
      },
      { label: 'EIN', value: val(props.form.tax_id), stepIndex: si('structure') },
      { label: 'Date established', value: val(props.form.date), stepIndex: si('structure') },
      { label: 'Entity address', value: addressLine.value, stepIndex: si('structure') },
    ],
  })

  // Administration
  const adminRows: ReviewRow[] = [
    { label: 'General Partner', value: val(props.form.fund_gp_name), stepIndex: si('admin') },
  ]
  if (props.isNewRecord) {
    adminRows.push({
      label: 'Management Company',
      value: val(props.form.company_name),
      stepIndex: si('admin'),
    })
  }
  adminRows.push(
    {
      label: 'Document signer',
      value: lookupLabel(props.adminOptions, props.form.admin_signer_id),
      stepIndex: si('admin'),
    },
    { label: 'Administrators', value: adminNames.value, stepIndex: si('admin') },
  )
  result.push({ title: 'Administration', rows: adminRows })

  // Structure
  result.push({
    title: 'Structure',
    rows: [
      {
        label: 'Management fee',
        value: formatPercent(props.form.management_fee_percentage),
        stepIndex: si('capital'),
      },
      { label: 'Carried interest', value: formatPercent(props.form.carried_interest), stepIndex: si('capital') },
      { label: 'Preferred return', value: formatPercent(props.form.preferred_return), stepIndex: si('capital') },
      {
        label: 'Capital call frequency',
        value: lookupLabel(props.capitalCallFrequencyOptions, props.form.capital_call_frequency),
        stepIndex: si('capital'),
      },
      {
        label: 'Allocation',
        value: lookupLabel(props.ownershipTypeOptions, props.form.ownership_type),
        stepIndex: si('capital'),
      },
      { label: 'Initial closing date', value: val(props.form.initial_closing_date), stepIndex: si('capital') },
      {
        label: 'Investment period end',
        value: val(props.form.investment_period_end_date),
        stepIndex: si('capital'),
      },
      { label: 'Waterfall', value: val(props.form.waterfall_calculation) || '—', stepIndex: si('capital') },
      {
        label: 'Interest catch-up',
        value: props.form.has_interest_catch_up_provision ? 'Yes' : 'No',
        stepIndex: si('capital'),
      },
    ],
  })

  // Raising
  const raisingRows: ReviewRow[] = [
    {
      label: 'Status',
      value:
        props.raisingStatus === 'currently_raising'
          ? 'Currently raising'
          : props.raisingStatus === 'closed'
            ? 'Closed / Not raising'
            : '—',
      stepIndex: si('raising'),
    },
  ]
  if (props.raisingStatus === 'currently_raising') {
    raisingRows.push(
      { label: 'Fund type', value: val(props.form.fund_type), stepIndex: si('raising') },
      {
        label: 'Thesis',
        value: props.form.company_description
          ? props.form.company_description.length > 60
            ? props.form.company_description.substring(0, 60) + '...'
            : props.form.company_description
          : '—',
        stepIndex: si('raising'),
      },
      {
        label: 'Target sectors',
        value: props.form.sectors?.length ? props.form.sectors.join(', ') : '—',
        stepIndex: si('raising'),
      },
      { label: 'Target stage', value: val(props.form.investment_stage), stepIndex: si('raising') },
      { label: 'Target raise', value: formatCurrency(props.form.raised_amount), stepIndex: si('raising') },
      {
        label: 'Minimum to Close',
        value: formatCurrency(props.form.pre_money_valuation_amount),
        stepIndex: si('raising'),
      },
      {
        label: 'Min commitment',
        value: formatCurrency(props.form.minimum_investment_amount),
        stepIndex: si('raising'),
      },
      { label: 'First close', value: val(props.form.expected_close_date), stepIndex: si('raising') },
    )
  }
  result.push({ title: 'Raising', rows: raisingRows })

  // Bank details
  const bankRows: ReviewRow[] = []
  if (props.form.is_venture360_the_administrator) {
    bankRows.push({
      label: 'Banking partner',
      value: props.form.is_requesting_bank_connection ? 'V360 will connect' : 'No',
      stepIndex: si('bank'),
    })
  }
  bankRows.push(
    { label: 'Bank name', value: val(props.form.disbursement_wire_bank_name), stepIndex: si('bank') },
    { label: 'Bank address', value: val(props.form.disbursement_wire_bank_address), stepIndex: si('bank') },
    {
      label: 'Routing number',
      value: val(props.form.disbursement_wire_bank_routing_number),
      stepIndex: si('bank'),
    },
    { label: 'SWIFT code', value: val(props.form.disbursement_wire_bank_swift_code), stepIndex: si('bank') },
    {
      label: 'Account number',
      value: props.form.disbursement_wire_bank_account_number ? '****' : '—',
      stepIndex: si('bank'),
    },
    { label: 'Account name', value: val(props.form.disbursement_wire_account_name), stepIndex: si('bank') },
    {
      label: 'For further credit to',
      value: val(props.form.disbursement_wire_for_further_credit_to),
      stepIndex: si('bank'),
    },
    {
      label: 'Wiring instructions',
      value: props.form.capital_call_wiring_instructions ? 'Provided' : '—',
      stepIndex: si('bank'),
    },
  )
  result.push({ title: 'Bank details', rows: bankRows })

  // Tax details (site admin only)
  if (authStore.is_site_admin) {
    const taxRows: ReviewRow[] = [
      {
        label: 'Tax return required',
        value: props.form.is_filing_required ? 'Yes' : 'No',
        stepIndex: si('tax'),
      },
    ]
    if (!props.form.is_filing_required) {
      taxRows.push({ label: 'Not filing reason', value: val(props.form.not_filing_reason), stepIndex: si('tax') })
    }
    if (props.form.is_filing_required) {
      taxRows.push(
        { label: 'Starting tax year', value: val(props.form.starting_tax_year), stepIndex: si('tax') },
        { label: 'Ending tax year', value: val(props.form.ending_tax_year), stepIndex: si('tax') },
        { label: 'Tax locator', value: val(props.form.gosystem_locator), stepIndex: si('tax') },
        { label: 'Tax basis', value: val(props.form.basis), stepIndex: si('tax') },
        { label: 'State filing', value: val(props.form.state_filing), stepIndex: si('tax') },
      )
    }
    result.push({ title: 'Tax details', rows: taxRows })
  }

  return result
})
</script>

<template>
  <div>
    <!-- Subscription summary card -->
    <OnboardingPlanSummary
      v-if="hasSubscription && selectedSubscription"
      :title="selectedSubscription.title"
      :price="selectedSubscription.price"
      :features="selectedSubscription.included"
      :add-ons="selectedAddOns"
      :total-due="totalDue"
      @click="emit('navigate', stepIndex('config'))"
    />

    <!-- Review sections -->
    <OnboardingReviewSection v-for="section in sections" :key="section.title" :title="section.title">
      <OnboardingReviewItem
        v-for="row in section.rows"
        :key="row.label"
        :label="row.label"
        :value="row.value"
        @click="emit('navigate', row.stepIndex)"
      />

      <!-- Supporting documents under Raising -->
      <OnboardingReviewItem
        v-if="section.title === 'Raising' && raisingStatus === 'currently_raising'"
        label="Supporting documents"
        :value="files.length || existingFiles?.length ? undefined : 'None'"
        :layout="files.length || existingFiles?.length ? 'stack' : 'row'"
        @click="emit('navigate', stepIndex('raising'))"
      >
        <template v-if="files.length">
          <div class="flex flex-col gap-1.5">
            <div v-for="(file, idx) in files" :key="idx" class="flex items-center gap-2">
              <VIcon name="FileText" class="h-3.5 w-3.5 shrink-0 text-[#009192]" />
              <span class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] text-gray-600">{{
                file.name
              }}</span>
              <span class="shrink-0 text-[11px] text-[#9ca3af]">{{ formatFileSize(file.size) }}</span>
            </div>
          </div>
        </template>
        <template v-else-if="existingFiles?.length">
          <div class="flex flex-col gap-1.5">
            <div v-for="(name, idx) in existingFiles" :key="idx" class="flex items-center gap-2">
              <VIcon name="FileText" class="h-3.5 w-3.5 shrink-0 text-[#009192]" />
              <span class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] text-gray-600">{{
                name
              }}</span>
            </div>
          </div>
        </template>
      </OnboardingReviewItem>
    </OnboardingReviewSection>

    <!-- Danger Zone -->
    <VSection v-if="isEditMode" label="Danger Zone" id="danger_zone" class="mt-24 scroll-mt-8">
      <div class="space-y-4">
        <VAlert type="error">
          Deleting this fund will permanently remove all associated data including commitments, capital calls,
          distributions, and documents. This action cannot be undone.
        </VAlert>
        <VButton variant="v-red" :click="() => emit('delete')">Delete Fund</VButton>
      </div>
    </VSection>
  </div>
</template>
